import { VerticalFlexbox } from "@src/components/layout/Flexbox";
import { Input } from "@src/components/ui/input";
import { Label } from "@src/components/ui/label";
import { cn } from "@src/lib/utils";
import { useField } from "formik";
import { useId, useMemo } from "react";

export const ContactCredentials = [
  "ACNP",
  "ACNP-BC",
  "ACNS",
  "ACNS-BC",
  "AGNP",
  "AGNP-C",
  "AGACNP",
  "AGACNP-BC",
  "AGPCNP",
  "AGPCNP-BC",
  "ANP",
  "ANP-BC",
  "ANP-C",
  "APN",
  "APNP",
  "APRN",
  "APRN-BC",
  "APRN-CNP",
  "APRN-Rx",
  "ARNP",
  "ATC",
  "BDS",
  "BS",
  "BSN",
  "CCRN",
  "CDCES",
  "CDE",
  "CEN",
  "CFNP",
  "CNP",
  "CNM",
  "CNS",
  "CPNP",
  "CPNP-PC",
  "CRNA",
  "CRNP",
  "CS",
  "DC",
  "DDS",
  "DMD",
  "DNP",
  "DO",
  "DPM",
  "DPT",
  "FNP",
  "FNP-BC",
  "FNP-C",
  "GNP",
  "LAc",
  "LCSW",
  "LD",
  "LMHC",
  "LPC",
  "LPN",
  "LMT",
  "LVN",
  "MA",
  "MBA",
  "MBBS",
  "MD",
  "MHS",
  "MMS",
  "MN",
  "MPAS",
  "MPH",
  "MS",
  "MSc",
  "MSD",
  "MSN",
  "MSW",
  "NCC",
  "ND",
  "NMD",
  "NP",
  "NP-BC",
  "NP-C",
  "OCN",
  "OD",
  "PA",
  "PA-C",
  "PharmD",
  "PhD",
  "PHN",
  "PMHNP",
  "PMHNP-BC",
  "PNP",
  "PT",
  "RD",
  "RN",
  "RN-BC",
  "RNFA",
  "RNC",
  "RPA",
  "RPA-C",
  "RPh",
  "WHCNP",
  "WHNP",
  "WHNP-BC",
] as const;

export interface CredentialsMindreaderProps {
  selectedValueFieldName: string;
  queryValueFieldName: string;
  label?: string;
  required?: boolean;
}

/**
 * Freeform credentials selector with autocomplete suggestions via a native
 * datalist. Stores the chosen value in Formik at `selectedValueFieldName`.
 */
export const CredentialsMindreader = ({
  selectedValueFieldName,
  label = "Credentials (optional)",
  required = false,
}: CredentialsMindreaderProps) => {
  const [field, meta, helpers] = useField<string | null>(
    selectedValueFieldName,
  );
  const listId = useId();
  const inputId = useId();
  const error = meta.touched && meta.error ? String(meta.error) : undefined;

  const items = useMemo(() => ContactCredentials, []);

  return (
    <VerticalFlexbox gap="xs" className="w-full">
      <Label htmlFor={inputId} className="text-sm text-foreground">
        {label}
        {required ? (
          <span className="ml-0.5 text-destructive" aria-hidden>
            *
          </span>
        ) : null}
      </Label>
      <Input
        id={inputId}
        name={selectedValueFieldName}
        list={listId}
        value={field.value ?? ""}
        onChange={(event) => helpers.setValue(event.target.value || null)}
        onBlur={() => helpers.setTouched(true)}
        placeholder="Start typing to search..."
        aria-invalid={error ? "true" : undefined}
        className={cn(error && "border-destructive ring-destructive")}
      />
      <datalist id={listId}>
        {items.map((credential) => (
          <option key={credential} value={credential} />
        ))}
      </datalist>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </VerticalFlexbox>
  );
};
